import React from 'react';
import { useTranslation } from 'react-i18next';

import { SvgAlertTriangle } from '@actual-app/components/icons/v2';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { HelpMenu } from '#components/HelpMenu';
import { LoggedInUser } from '#components/LoggedInUser';
import { useServerURL } from '#components/ServerContext';
import { PrivacyButton, ServerSyncButton } from '#components/Titlebar';
import { useSheetValue } from '#hooks/useSheetValue';
import * as bindings from '#spreadsheet/bindings';

import { Item } from './Item';
import { useSidebar } from './SidebarProvider';

const buttonStyle = { color: theme.sidebarItemText };

function UncategorizedItem() {
  const { t } = useTranslation();
  const count: number | null = useSheetValue(bindings.uncategorizedCount());
  if (count === null || count <= 0) {
    return null;
  }

  return (
    <Item
      title={t('{{count}} uncategorized transactions', { count })}
      Icon={SvgAlertTriangle}
      to="/categories/uncategorized"
    />
  );
}

// Controls that used to live in the top bar. Hidden when the sidebar floats
// (narrow windows), where the top bar shows them instead.
export function SidebarUtilities() {
  const { collapsed, alwaysFloats } = useSidebar();
  const serverURL = useServerURL();

  if (alwaysFloats) {
    return null;
  }

  return (
    <View
      style={{
        flexShrink: 0,
        alignItems: collapsed ? 'center' : 'stretch',
        padding: collapsed ? '4px 0' : '4px 8px',
        color: theme.sidebarItemText,
      }}
    >
      <UncategorizedItem />
      <View
        style={{
          flexDirection: collapsed ? 'column' : 'row',
          alignItems: 'center',
          gap: collapsed ? 2 : 4,
        }}
      >
        <PrivacyButton style={buttonStyle} />
        {serverURL ? (
          <ServerSyncButton
            style={{ color: theme.sidebarItemText }}
            baseColor={theme.sidebarItemText}
          />
        ) : null}
        {collapsed && (
          <LoggedInUser
            compact
            buttonStyle={buttonStyle}
            menuPlacement="right bottom"
          />
        )}
        <HelpMenu
          compact={collapsed}
          placement={collapsed ? 'right bottom' : 'top start'}
          buttonStyle={buttonStyle}
        />
      </View>
      {!collapsed && (
        <LoggedInUser
          style={{ flexWrap: 'wrap', marginTop: 2 }}
          buttonStyle={buttonStyle}
          menuPlacement="top start"
        />
      )}
    </View>
  );
}
